import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip,
  Button,
  ButtonGroup,
  Flex,
  Text,
  Spinner,
  Alert,
  AlertIcon
} from "@chakra-ui/react";
import { GrView } from "react-icons/gr";
import { useNavigate } from "react-router-dom";
import { getAllChannelPartners, getTpaWallet } from "../services/Wallet/channelPartnersService";
import { getPageItems } from '../utils/pagination';
import { useWallet } from "contexts/WalletContext";

const ChannelPartnerTable = ({ searchQuery }) => {
  const navigate = useNavigate();
  const { setWallet } = useWallet();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const role = localStorage.getItem('authRole');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 5;
  const fetchInFlightRef = useRef(null);

  const fetchChannelPartners = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const { items: data, pagination } = ['finance_user', 'wallet_superadmin', 'wallet_finance'].includes(role) ? await getAllChannelPartners(page, itemsPerPage) : await getTpaWallet(page, itemsPerPage)

      const enrichedData = data.map((item) => ({
        id: item.id,
        name: item.anchor,
        balance: item.amount,
        lasttopup: item.last_topup ?? "N/A",
        status: item.amount <= item.minimum_amount ? "Low" : "Active",
        walletId: item?.wallet_id
      }));

      setItems(enrichedData);
      setTotalPages(Math.ceil(pagination.total / itemsPerPage));
      setTotalItems(pagination.total);

    } catch (error) {
      console.error("Error fetching channel partners:", error);
      setError("Failed to load channel partners. Please try again.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [role, itemsPerPage]);

  useEffect(() => {
    const requestKey = `${currentPage}-${itemsPerPage}`;
    if (fetchInFlightRef.current === requestKey) return;
    fetchInFlightRef.current = requestKey;
    fetchChannelPartners(currentPage).finally(() => {
      fetchInFlightRef.current = null;
    });
  }, [currentPage, fetchChannelPartners, itemsPerPage]);

  const handleView = (id, walletId) => {
    setWallet(prev => ({
      ...prev,
      anchorId: id,
      anchorWalletId: walletId
    }));
    navigate(`/partner-detail/${id}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Filter items based on search query (client-side filtering for current page)
  const filteredItems = items.filter((item) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(q) ||
      String(item.balance).includes(q) ||
      item.status.toLowerCase().includes(q)
    );
  });

  const formatDate = (dateString) => {
    if (dateString === "N/A") return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return "N/A";
    }
  };

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <>
      <TableContainer>
        <Table variant="outline" size="md">
          <Thead>
            <Tr bg="#F9FAFB" className="font-semibold text-black">
              <Th>Channel Partner</Th>
              <Th>Wallet Balance</Th>
              <Th>Last Topup</Th>
              <Th>Status</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody className="text-sm">
            {loading ? (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Spinner size="md" />
                </Td>
              </Tr>
            ) : filteredItems.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  No channel partners found
                </Td>
              </Tr>
            ) : (
              filteredItems.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.name}</Td>
                  <Td>{item.balance?.toFixed(2)}</Td>
                  <Td>{formatDate(item.lasttopup)}</Td>
                  <Td>
                    <Text
                      color={item.status === "Low" ? "red.500" : "green.500"}
                      fontWeight="medium"
                    >
                      {item.status}
                    </Text>
                  </Td>
                  <Td>
                    <Tooltip label="View">
                      <button onClick={() => handleView(item.id, item.walletId)}>
                        <GrView />
                      </button>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <Flex justify="space-between" align="center" mt={4}>
          <Text>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
          </Text>
          <ButtonGroup spacing={2}>
            <Button
              size="sm"
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              isDisabled={currentPage === 1}
            >
              Prev
            </Button>

            {getPageItems(currentPage, totalPages, 1).map((item, idx) =>
              item === 'ellipsis' ? (
                <Button key={`e-${idx}`} size="sm" isDisabled variant="ghost">
                  …
                </Button>
              ) : (
                <Button
                  key={item}
                  size="sm"
                  variant={currentPage === item ? "solid" : "outline"}
                  onClick={() => handlePageChange(item)}
                >
                  {item}
                </Button>
              )
            )}

            <Button
              size="sm"
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </ButtonGroup>
        </Flex>
      )}
    </>
  );
};

export default ChannelPartnerTable;
